import { useTranslation } from 'react-i18next';
import {
  StrategyDescription,
  CustomSalaryStrategy,
  CustomSalaryStrategyDescription,
  SalaryStrategy,
  toggleTarget,
  findDescription,
} from '../../../../app/spraypaint/types/salaryStrategyTypes';
import { MoneyType } from '../../../common/utils/type.utils';
import { PayslipChoiceCard } from './PayslipChoiceCard';
import { PayslipCustomChoiceMinusCard } from '../../components/PayslipCustomChoiceMinusCard';
import { useSetDefaultOrLast } from '../hooks/useSetDefaultOrLast';
import { containerChoiceCard } from '../../constantes/tailwindStyle';

interface CustomChoiceCardProps {
  onStrategyChange: (strategy: CustomSalaryStrategy) => void;
  currentStrategy: SalaryStrategy | undefined;
  availableStrategiesDescription: StrategyDescription[];
  handleSetError: (value: boolean) => void;
}

export const CustomChoiceCard = ({
  onStrategyChange,
  currentStrategy,
  availableStrategiesDescription,
  handleSetError,
}: CustomChoiceCardProps) => {
  const { t } = useTranslation();
  const isActive = currentStrategy?.name === 'custom_salary';

  const customSalaryDescription =
    findDescription<CustomSalaryStrategyDescription>(
      'custom_salary',
      availableStrategiesDescription,
    );

  const maxGrossSalary = customSalaryDescription?.variables?.maximum.gross || {
    amount: 0,
  };

  const { handleClick, defaultOrLast } = useSetDefaultOrLast(
    {
      name: 'custom_salary',
      options: {
        value: maxGrossSalary,
        target: 'gross',
      },
    },
    currentStrategy,
    onStrategyChange,
  );

  if (!customSalaryDescription) return null;
  const minGrossSalary = customSalaryDescription.variables.minimum.gross;
  const maxNetSalary = customSalaryDescription.variables.maximum.net;
  const minNetSalary = customSalaryDescription.variables.minimum.net;

  const handleSwitchChange = () => {
    if (!isActive) return;

    onStrategyChange({
      name: 'custom_salary',
      options: {
        ...currentStrategy.options,
        target: toggleTarget(currentStrategy.options.target),
      },
    });
  };

  const handleInputChange = (value: number) => {
    if (!isActive) return;

    onStrategyChange({
      name: 'custom_salary',
      options: {
        ...currentStrategy.options,
        value: {
          ...currentStrategy.options.value,
          amount: value,
        },
      },
    });
  };

  const displayValue: MoneyType = isActive
    ? currentStrategy.options.value
    : defaultOrLast.options.value;
  const switchOptions = [
    {
      label: t(
        'front.payslip.tdc.salaryChoice.custom_salary.gross',
      ).toUpperCase(),
      value: 'gross',
    },
    {
      label: t(
        'front.payslip.tdc.salaryChoice.custom_salary.net',
      ).toUpperCase(),
      value: 'net',
    },
  ];

  return (
    <div className={containerChoiceCard}>
      <PayslipChoiceCard
        strategyName="custom_salary"
        value={displayValue}
        isActive={isActive}
        onClick={handleClick}
        tooltip
      />
      {isActive && (
        <PayslipCustomChoiceMinusCard
          maxGrossSalary={maxGrossSalary}
          minGrossSalary={minGrossSalary}
          maxNetSalary={maxNetSalary}
          minNetSalary={minNetSalary}
          handleSetError={handleSetError}
          intervalMinText={t('front.inputNumber.intervalMinText') as string}
          inputValue={currentStrategy.options.value.amount}
          onChangeInput={handleInputChange}
          handleSwitchChange={handleSwitchChange}
          switchOptions={switchOptions}
          selectedOption={currentStrategy.options.target}
        />
      )}
    </div>
  );
};
